'use client'

import { apiFetch, buildApiWebSocketUrl } from '@/lib/api-client'
import { useState, useEffect, useRef } from 'react'
import { Bell, X, Info, CheckCircle, AlertTriangle, XCircle, MessageCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { ScrollArea } from '@/components/ui/scroll-area'
import { useRouter } from '@/lib/react-compat/navigation'
import { useAuth } from '@/components/auth-context'
import { logger } from '@/lib/logger'

const LOG_SCOPE = '[NotificationCenter]'

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error)
}

export type NotificationType = 'info' | 'success' | 'warning' | 'error'

export interface Notification {
  id: string
  title: string
  message: string
  type: NotificationType
  timestamp: Date
  read: boolean
  actionType?: 'crop-recommendation' | 'disease-detection'
  data?: any
  source?: 'local' | 'server'
}

const notificationIcons = {
  info: Info,
  success: CheckCircle,
  warning: AlertTriangle,
  error: XCircle,
}

const notificationColors = {
  info: 'text-blue-600 bg-blue-50',
  success: 'text-green-600 bg-green-50',
  warning: 'text-yellow-600 bg-yellow-50',
  error: 'text-red-600 bg-red-50',
}

export function NotificationCenter() {
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const [isConnected, setIsConnected] = useState(false)
  const wsRef = useRef<WebSocket | null>(null)
  const router = useRouter()
  const { isAuthenticated, loading } = useAuth()
  const shouldRender = !loading && isAuthenticated

  useEffect(() => {
    if (!shouldRender) {
      return
    }
    // 从服务器加载通知的函数
    const loadNotificationsFromServer = async () => {
      try {
        const response = await apiFetch('/api/notifications')
        if (response.ok) {
          const result = await response.json()

          if (result.success && result.data) {
            // 转换服务器端通知为客户端格式
            const serverNotifications = result.data.map((n: any) => ({
              ...n,
              timestamp: new Date(n.timestamp),
            }))

            // 合并服务器端通知和localStorage通知（避免重复）
            const localNotifications = JSON.parse(
              localStorage.getItem('notifications') || '[]'
            ).map((n: any) => ({
              ...n,
              timestamp: new Date(n.timestamp),
              source: 'local',
            }))

            // 按时间戳去重合并
            const allNotifications = [...serverNotifications, ...localNotifications]
            const uniqueNotifications = allNotifications
              .filter(
                (notification, index, self) =>
                  index === self.findIndex((n) => n.id === notification.id)
              )
              .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())

            if (uniqueNotifications.length > 0) {
              logger.debug(LOG_SCOPE, '通知加载完成', {
                serverCount: serverNotifications.length,
                localCount: localNotifications.length,
                totalCount: uniqueNotifications.length,
              })
            }
            setNotifications(uniqueNotifications)
          } else {
            logger.warn(LOG_SCOPE, '通知接口返回格式异常')
          }
        } else {
          logger.error(LOG_SCOPE, '通知接口请求失败', {
            status: response.status,
            statusText: response.statusText,
          })
        }
      } catch (error) {
        logger.error(LOG_SCOPE, '加载服务器通知失败', errorMessage(error))
        // 降级到只使用localStorage
        try {
          const stored = localStorage.getItem('notifications')
          if (stored) {
            const parsed = JSON.parse(stored).map((n: any) => ({
              ...n,
              timestamp: new Date(n.timestamp),
            }))
            setNotifications(parsed)
          }
        } catch (localError) {
          logger.error(LOG_SCOPE, '加载本地通知失败', errorMessage(localError))
        }
      }
    }

    const setupWSConnection = (): (() => void) => {
      let disposed = false
      let reconnectTimer: number | null = null

      const handleHistorical = (historicalNotifications: any[]) => {
        try {
          if (historicalNotifications.length > 0) {
            logger.debug(LOG_SCOPE, '收到历史通知', {
              count: historicalNotifications.length,
            })
          }

          const formattedNotifications = historicalNotifications.map((n: any) => ({
            ...n,
            timestamp: new Date(n.timestamp),
            source: 'server',
          }))

          setNotifications((prev) => {
            const merged = [...formattedNotifications, ...prev]
            const unique = merged.filter(
              (notification, index, arr) => index === arr.findIndex((n) => n.id === notification.id)
            )
            return unique
          })
        } catch (e) {
          logger.warn(LOG_SCOPE, '解析历史通知失败', errorMessage(e))
        }
      }

      const handleNew = (newNotification: any) => {
        try {
          logger.debug(LOG_SCOPE, '收到新通知', {
            id: newNotification.id,
            title: newNotification.title,
          })

          const formattedNotification = {
            ...newNotification,
            timestamp: new Date(newNotification.timestamp),
            source: 'server',
          }

          setNotifications((prev) => {
            const exists = prev.find((n) => n.id === formattedNotification.id)
            if (exists) return prev
            return [formattedNotification, ...prev]
          })
        } catch (e) {
          logger.warn(LOG_SCOPE, '解析新通知失败', errorMessage(e))
        }
      }

      const connect = () => {
        if (disposed) return
        const ws = new WebSocket(buildApiWebSocketUrl('/api/notifications/ws'))
        wsRef.current = ws

        ws.onopen = () => {
          logger.info(LOG_SCOPE, 'WebSocket 已连接')
          setIsConnected(true)
        }

        ws.onmessage = (e) => {
          try {
            const { event, data } = JSON.parse(e.data)
            if (event === 'historical_notifications' && Array.isArray(data)) {
              handleHistorical(data)
            } else if (event === 'new_notification' && data) {
              handleNew(data)
            }
          } catch (err) {
            logger.warn(LOG_SCOPE, '解析 WebSocket 消息失败', errorMessage(err))
          }
        }

        ws.onerror = () => {
          logger.warn(LOG_SCOPE, 'WebSocket 连接异常')
          setIsConnected(false)
        }

        ws.onclose = () => {
          setIsConnected(false)
          if (!disposed) {
            reconnectTimer = window.setTimeout(connect, 3000)
          }
        }
      }

      connect()

      return () => {
        disposed = true
        if (reconnectTimer !== null) {
          window.clearTimeout(reconnectTimer)
        }
        try {
          wsRef.current?.close()
        } catch {
          /* ignore */
        }
        wsRef.current = null
      }
    }

    // 先加载历史通知，然后建立 WebSocket 连接
    loadNotificationsFromServer()
    const cleanup = setupWSConnection()

    return cleanup
  }, [shouldRender])

  if (!shouldRender) {
    return null
  }

  const unreadCount = notifications.filter((n) => !n.read).length

  const markAsRead = async (id: string) => {
    // 立即更新本地状态
    const updatedNotifications = notifications.map((n) => (n.id === id ? { ...n, read: true } : n))
    setNotifications(updatedNotifications)

    // 如果是localStorage通知，也更新localStorage
    const localNotifications = JSON.parse(localStorage.getItem('notifications') || '[]')
    const localUpdated = localNotifications.map((n: any) =>
      n.id === id ? { ...n, read: true } : n
    )
    localStorage.setItem('notifications', JSON.stringify(localUpdated))

    // 尝试同步到服务器（对服务器端通知）
    try {
      await apiFetch('/api/notifications', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'mark_read', ids: id }),
      })
    } catch (error) {
      logger.warn(LOG_SCOPE, '同步已读状态失败', errorMessage(error))
    }
  }

  const markAllAsRead = async () => {
    // 立即更新本地状态
    const updatedNotifications = notifications.map((n) => ({ ...n, read: true }))
    setNotifications(updatedNotifications)

    // 更新localStorage
    const localNotifications = JSON.parse(localStorage.getItem('notifications') || '[]')
    const localUpdated = localNotifications.map((n: any) => ({ ...n, read: true }))
    localStorage.setItem('notifications', JSON.stringify(localUpdated))

    // 尝试同步到服务器
    try {
      const serverNotificationIds = notifications
        .filter((n) => !n.source || n.source !== 'local')
        .map((n) => n.id)
      if (serverNotificationIds.length > 0) {
        await apiFetch('/api/notifications', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'mark_read', ids: serverNotificationIds }),
        })
      }
    } catch (error) {
      logger.warn(LOG_SCOPE, '同步全部已读状态失败', errorMessage(error))
    }
  }

  const removeNotification = async (id: string) => {
    // 立即更新本地状态
    const updatedNotifications = notifications.filter((n) => n.id !== id)
    setNotifications(updatedNotifications)

    // 如果是localStorage通知，也从localStorage删除
    const localNotifications = JSON.parse(localStorage.getItem('notifications') || '[]')
    const localUpdated = localNotifications.filter((n: any) => n.id !== id)
    localStorage.setItem('notifications', JSON.stringify(localUpdated))

    // 尝试从服务器删除
    try {
      await apiFetch('/api/notifications', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'delete', ids: id }),
      })
    } catch (error) {
      logger.warn(LOG_SCOPE, '删除服务器通知失败', errorMessage(error))
    }
  }

  const handleNotificationClick = (notification: Notification) => {
    // 只标记为已读，不要删除通知
    markAsRead(notification.id)

    if (notification.actionType && notification.data) {
      if (notification.actionType === 'crop-recommendation') {
        const params = new URLSearchParams(notification.data)
        router.push(`/recommendation?${params.toString()}&fromNotification=true`)
      } else if (notification.actionType === 'disease-detection') {
        try {
          sessionStorage.setItem('uploadedImage', JSON.stringify(notification.data))
        } catch (e) {
          logger.warn(LOG_SCOPE, '保存通知图片失败', errorMessage(e))
        }
        router.push(`/disease-detection?fromNotification=true&t=${Date.now()}`)
      }
      // 关闭通知面板，但通知依然保留在列表中
      setIsOpen(false)
    }
  }

  const formatTime = (date: Date) => {
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / (1000 * 60))
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

    if (diffMins < 1) return '刚刚'
    if (diffMins < 60) return `${diffMins}分钟前`
    if (diffHours < 24) return `${diffHours}小时前`
    if (diffDays < 7) return `${diffDays}天前`
    return date.toLocaleDateString('zh-CN', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="relative">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge
              variant="destructive"
              className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center text-xs"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-80 p-0" align="end">
        <div className="border-b px-4 py-3">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-sm">通知中心</h3>
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={markAllAsRead}
                className="text-xs h-6 px-2"
              >
                全部已读
              </Button>
            )}
          </div>
        </div>

        <ScrollArea className="h-96">
          {isConnected && (
            <div className="px-4 py-2 bg-green-50 border-b text-xs text-green-700">WS已连接</div>
          )}
          {notifications.length === 0 ? (
            <div className="text-center text-gray-500 text-sm py-12">
              <MessageCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
              暂无通知
            </div>
          ) : (
            <div className="space-y-0">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`group relative px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 ${
                    !notification.read ? 'bg-blue-50/30' : ''
                  }`}
                  onClick={() => {
                    markAsRead(notification.id)
                    handleNotificationClick(notification)
                  }}
                >
                  <div className="flex gap-3 pr-6">
                    {/* 左侧图标区域 + 未读指示点 */}
                    <div className="relative shrink-0">
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center ${notificationColors[notification.type]}`}
                      >
                        {(() => {
                          const Icon = notificationIcons[notification.type] || Info
                          return <Icon className="h-4 w-4" />
                        })()}
                      </div>
                      {/* 未读蓝点 - 显示在图标右上角 */}
                      {!notification.read && (
                        <div className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 bg-blue-500 rounded-full border-2 border-white" />
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <p
                          className={`font-medium text-sm ${!notification.read ? 'text-gray-900' : 'text-gray-700'}`}
                        >
                          {notification.title}
                        </p>
                      </div>
                      <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                        {notification.message}
                      </p>
                      <div className="flex items-center justify-between mt-1">
                        <p className="text-xs text-gray-400">
                          {formatTime(notification.timestamp)}
                        </p>
                        {notification.source === 'server' && (
                          <Badge variant="outline" className="text-xs px-1.5 py-0.5 h-auto">
                            服务器
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* 关闭按钮 - 独立放在右上角 */}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="absolute top-2 right-2 h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-gray-200"
                    onClick={(e) => {
                      e.stopPropagation()
                      removeNotification(notification.id)
                    }}
                  >
                    <X className="h-3.5 w-3.5 text-gray-500" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>

        {notifications.length > 0 && (
          <div className="border-t px-4 py-2">
            <Button
              variant="ghost"
              size="sm"
              className="w-full text-xs h-7"
              onClick={() => setIsOpen(false)}
            >
              查看全部通知
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}
